import { useAuthState, useRedirect } from 'react-admin';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { CustomAppBar } from './components/CustomAppBar';
import { CustomMenu } from './components/CustomMenu';
import { TenantProvider } from './contexts/TenantContext';
import { Box } from '@mui/material';

export const CustomLayout = ({ children, ...rest }: any) => {
    const { authenticated } = useAuthState();
    const redirect = useRedirect();
    const location = useLocation();

    useEffect(() => {
        // Check if user is standalone (no tenants) and redirect them
        const user = localStorage.getItem('user');
        if (user) {
            try {
                const userData = JSON.parse(user);
                const hasTenants = userData.tenants && userData.tenants.length > 0;

                if (!hasTenants) {
                    // User has no tenants, redirect to standalone dashboard
                    localStorage.setItem('isStandalone', 'true');
                    window.location.href = '/standalone-dashboard';
                    return;
                }

                // The real access boundary is the tenant Role enum (admin/operator/user),
                // not the userRole signup PERSONA flag (driver/owner_operator/company) —
                // a driver-persona user can also genuinely be an admin/operator, and a
                // plain "user"-role tenant member (e.g. a driver account that was never
                // tagged with the driver persona) is just as unauthorized for admin pages.
                // Anyone who isn't company/superAdmin/admin/operator gets the same
                // restriction, regardless of their persona flag (mirrors the same checks
                // CustomMenu.tsx uses to decide what to show in the nav).
                const isCompanyUser = userData.userRole === 'company';
                const isSuperAdmin = userData.isSuperAdmin === true;
                const isAdminOrOperator = Array.isArray(userData.roles) && (userData.roles.includes('admin') || userData.roles.includes('operator'));
                const isRestrictedUser = !isCompanyUser && !isSuperAdmin && !isAdminOrOperator;

                // Restricted users (drivers and any other non-admin/operator tenant
                // member) can log in (they need to, e.g. to finish onboarding via
                // /drivers/wizard or /drivers/profile — those are separate, no-layout
                // routes this effect never runs for), but every route that reaches
                // THIS layout is admin-dashboard content, which they should never see.
                // Redirect them to the one layout route they're allowed — completed-forms
                // is scoped to their own submissions server-side — from anywhere else
                // under the layout, not just '/'.
                // Uses replace() (not .href) so this doesn't add a history entry —
                // otherwise Back would bounce right back here.
                const isAllowedRestrictedPath = location.pathname.startsWith('/completed-forms');
                if (isRestrictedUser && !isAllowedRestrictedPath) {
                    window.location.replace('/completed-forms');
                }
            } catch (e) {
                console.error('Error parsing user data:', e);
            }
        }
    }, [location.pathname]);

    if (!authenticated) {
        redirect('/login');
        return null;
    }

    // Create a completely custom layout without using React Admin's Layout
    return (
        <TenantProvider>
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: '100vh',
                backgroundColor: '#F5F7FA',
                fontFamily: 'Roboto',
            }}>
                {/* App bar at the top */}
                <CustomAppBar {...rest} />

                {/* Main content area with sidebar */}
                <Box sx={{
                    display: 'flex',
                    flex: 1,
                }}>
                    {/* Side menu */}
                    <CustomMenu {...rest} />

                    {/* Main content */}
                    <Box sx={{
                        flex: 1,
                        padding: '16px',
                        marginLeft: '256px', /* Menu width (240px) + left margin (16px) */
                        backgroundColor: '#F5F7FA',
                    }}>
                        {children}
                    </Box>
                </Box>
            </Box>
        </TenantProvider>
    );
};